import { useEffect, useRef, type RefObject } from 'react';

/**
 * 지금 열려 있는 모달 목록(열린 순서대로).
 *
 * 모달이 여러 개 겹쳐 있을 때 ESC 를 한 번 누르면 맨 위 모달 하나만 닫히게 하려고 쓴다.
 * 이게 없으면 열린 모달이 모두 ESC 를 받아서 한꺼번에 닫힌다.
 *
 * 확인·알림창(showConfirm·showAlert)도 이 목록에 들어간다.
 * 그래서 확인창에서 ESC 를 누르면 확인창만 닫히고, 그 아래 모달은 그대로 남는다.
 */
const layers: symbol[] = [];

interface ModalLayer {
  isTop: () => boolean;
  release: () => void;
}

function pushModalLayer(): ModalLayer {
  const id = Symbol('modal-layer');
  layers.push(id);
  let released = false;
  return {
    isTop: () => layers[layers.length - 1] === id,
    release: () => {
      // 두 번 불려도 한 번만 빠지게 한다.
      if (released) return;
      released = true;
      const index = layers.indexOf(id);
      if (index >= 0) layers.splice(index, 1);
    },
  };
}

/**
 * 모달이 열려 있는 동안 목록에 올려 둔다.
 * ESC 를 받았을 때 `isTop()` 이 true 일 때만 닫으면 된다.
 *
 * `open` 이 바뀔 때만 다시 등록한다. 다른 값까지 보면 화면이 다시 그려질 때마다
 * 목록 맨 뒤로 옮겨져서, 아래 있던 모달이 맨 위인 것처럼 된다.
 */
export function useModalLayer(open: boolean): RefObject<ModalLayer | null> {
  const layerRef = useRef<ModalLayer | null>(null);
  useEffect(() => {
    if (!open) return;
    const layer = pushModalLayer();
    layerRef.current = layer;
    return () => {
      layer.release();
      if (layerRef.current === layer) layerRef.current = null;
    };
  }, [open]);
  return layerRef;
}
